import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useProjectWorkOrders } from "@/hooks/useProjectContracts";
import { useAssignUnits } from "@/hooks/useUnitSchedule";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  projectId: string;
  sovLineId: string;
  sovLines: any[];
  units: any[];
  scopeItems: any[];
  assignments: any[];
  onComplete: () => void;
}

export const UnitAssignmentPanel = ({
  open, onOpenChange, projectId, sovLineId,
  sovLines, units, scopeItems, assignments, onComplete,
}: Props) => {
  const { data: workOrders } = useProjectWorkOrders(projectId);
  const assignUnits = useAssignUnits();
  const [selectedWOId, setSelectedWOId] = useState("");
  const [selectedUnitIds, setSelectedUnitIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const sovLine = sovLines.find((sl: any) => sl.id === sovLineId);

  // Units that have scope for this SOV line
  const unitsWithScope = useMemo(() => {
    return units
      .map((unit: any) => {
        const si = scopeItems.find((s: any) => s.unit_id === unit.id && s.sov_line_id === sovLineId && s.has_scope);
        if (!si) return null;
        // Match against work_order_lines by unit_id + sov_line_id
        const existingAssignment = assignments.find(
          (a: any) => a.unit_id === unit.id && a.sov_line_id === sovLineId
        );
        return { unit, scopeItem: si, assignment: existingAssignment };
      })
      .filter(Boolean) as Array<{ unit: any; scopeItem: any; assignment: any }>;
  }, [units, scopeItems, assignments, sovLineId]);

  const filtered = search
    ? unitsWithScope.filter(u => u.unit.unit_number.toLowerCase().includes(search.toLowerCase()))
    : unitsWithScope;

  const selectedWO = workOrders?.find((wo: any) => wo.id === selectedWOId);

  const toggleUnit = (unitId: string) => {
    setSelectedUnitIds(prev => {
      const next = new Set(prev);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });
  };

  const selectAll = () => {
    const assignable = filtered.filter(u => !u.assignment);
    setSelectedUnitIds(new Set(assignable.map(u => u.unit.id)));
  };

  const selectedTotal = unitsWithScope
    .filter(u => selectedUnitIds.has(u.unit.id))
    .reduce((s, u) => s + Number(u.scopeItem.quantity), 0);

  const handleAssign = async () => {
    if (!selectedWOId || selectedUnitIds.size === 0) return;
    setBusy(true);
    try {
      const { data: companyId } = await supabase.rpc("my_company_id");
      if (!companyId) throw new Error("Company not found");

      const wo = workOrders?.find((w: any) => w.id === selectedWOId);
      const assignmentRows = unitsWithScope
        .filter(u => selectedUnitIds.has(u.unit.id) && !u.assignment)
        .map(u => ({
          unit_id: u.unit.id,
          unit_scope_item_id: u.scopeItem.id,
          sov_line_id: sovLineId,
          work_order_id: selectedWOId,
          vendor_id: wo?.contractor_id,
        }));

      await assignUnits.mutateAsync({
        companyId,
        projectId,
        assignments: assignmentRows,
      });

      toast.success(`${assignmentRows.length} units assigned`);
      setSelectedUnitIds(new Set());
      onComplete();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assign Units — {sovLine?.description}</DialogTitle>
          <DialogDescription>
            Total: {fmt(Number(sovLine?.quantity ?? 0))} {sovLine?.unit} across {unitsWithScope.length} units
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* WO Selector */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Work Order</label>
            <Select value={selectedWOId} onValueChange={setSelectedWOId}>
              <SelectTrigger>
                <SelectValue placeholder="Select work order" />
              </SelectTrigger>
              <SelectContent>
                {(workOrders ?? []).map((wo: any) => (
                  <SelectItem key={wo.id} value={wo.id}>
                    {wo.number} — {wo.vendors?.name ?? wo.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Search + Select All */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search units..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8" />
            </div>
            <Button variant="outline" size="sm" onClick={selectAll}>Select All</Button>
            <Button variant="outline" size="sm" onClick={() => setSelectedUnitIds(new Set())}>Clear</Button>
          </div>

          {/* Unit List */}
          <ScrollArea className="max-h-[300px]">
            <div className="space-y-1">
              {filtered.map(({ unit, scopeItem, assignment }) => {
                const isAssigned = !!assignment;
                const isSelected = selectedUnitIds.has(unit.id);
                    const isSameWO = assignment?.work_order_id === selectedWOId;

                return (
                  <div
                    key={unit.id}
                    className={cn(
                      "flex items-center gap-3 p-2 rounded-md text-sm",
                      isAssigned && !isSameWO && "bg-destructive/5",
                      isSelected && "bg-primary/5"
                    )}
                  >
                    <Checkbox
                      checked={isSelected || (isAssigned && isSameWO)}
                      disabled={isAssigned}
                      onCheckedChange={() => toggleUnit(unit.id)}
                    />
                    <span className="font-mono font-medium">Unit {unit.unit_number}</span>
                    <span className="text-muted-foreground font-mono">
                      {fmt(Number(scopeItem.quantity))} {sovLine?.unit}
                    </span>
                    {isAssigned && (
                      <Badge variant="outline" className="text-xs ml-auto">
                        {assignment.work_orders?.number ?? "Assigned"}
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {/* Summary */}
          <div className="flex justify-between text-sm text-muted-foreground border-t pt-2">
            <span>Selected: {selectedUnitIds.size} units | {fmt(selectedTotal)} {sovLine?.unit}</span>
            <span>Already assigned: {unitsWithScope.filter(u => u.assignment).length}</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleAssign} disabled={busy || selectedUnitIds.size === 0 || !selectedWOId}>
            {busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" /> Assigning…</> : `Assign ${selectedUnitIds.size} Units →`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
